package com.siriusxm.pia.views.chat

import androidx.compose.runtime.Composable
import com.siriusxm.cmc.utils.Route
import com.siriusxm.pia.ApplicationContext
import com.siriusxm.pia.ConsoleApplication
import com.siriusxm.unifiedcontent.cmc.CMCService
import io.ktor.client.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.serialization.json.Json

/**
 * Top-level chat application module for the CMC.
 *
 * Provides an AI-powered conversational interface backed by the CMC chat API
 * (POST /chat, GET /chat/{id}, GET /chat). The LLM processing runs async in a
 * worker Lambda so the UI polls for results.
 *
 * Routes:
 * - /chat                        → ConversationView with no active conversation
 * - /chat/{id}                   → ConversationView with the specified conversation loaded
 * - /chat/transcripts            → admin transcript index (unlisted; AVP-gated server-side)
 * - /chat/transcripts/{id}       → one conversation's full agentic trace
 */
class ChatApplication : ConsoleApplication {
    lateinit var context: ApplicationContext
    lateinit var cmcService: CMCService
    private var initialized = false

    override suspend fun initialize(context: ApplicationContext) {
        if (initialized) return
        initialized = true
        this.context = context
        cmcService = CMCService(
            context.configuration.cmcApiUrl!!,
            HttpClient {
                install(ContentNegotiation) {
                    json(Json { isLenient = true; ignoreUnknownKeys = true })
                }
                expectSuccess = false
                context.installAuth(this)
            }
        )
    }

    /**
     * Dispatches every `/chat` URL to one of two screens, keyed off whether the first path segment
     * is the reserved word `transcripts`.
     *
     * The live-chat branch renders [ConversationView] from a *single* call site with the conversation
     * id passed as a parameter — deliberately not a `switch` with one branch per URL shape: separate
     * branches are separate composition slots, so moving between `/chat` and `/chat/{id}` would
     * unmount and remount the view, discarding the messages already on screen and the state of the
     * request that produced the new id. With one call site the id is just an input the view reacts to.
     *
     * The `transcripts` branch is the unlisted admin review UI ([TranscriptsScreen]). It has its own
     * composition slot because it is an entirely different screen, not a variant of the chat view, so
     * there is nothing to preserve across the boundary. Reaching it is a UI affordance only: the API
     * operations it calls are gated server-side by the unified-content AVP action
     * `SXM::UnifiedContentManagementConsole_ViewChatTranscripts`, and the screen renders a
     * "not authorized" state when the API refuses — the route gate is never treated as the
     * authorization boundary.
     */
    @Composable
    override fun route(route: Route) {
        val firstSegment = route.remainingPath.substringBefore("/")
        if (firstSegment == TRANSCRIPTS_SEGMENT) {
            // Everything after `transcripts/` is the (optional) conversation id for the detail view.
            val transcriptId = route.remainingPath.substringAfter("/", "")
                .substringBefore("/")
                .takeIf { CONVERSATION_ID.matches(it) }
            TranscriptsScreen(app = this, conversationId = transcriptId)
        } else {
            val conversationId = firstSegment.takeIf { CONVERSATION_ID.matches(it) }
            ConversationView(app = this, conversationId = conversationId)
        }
    }

    private companion object {
        /** A conversation id in the path is a UUID; anything else is treated as no conversation. */
        val CONVERSATION_ID = Regex("[a-f0-9-]{36}")

        /** Reserved first path segment selecting the admin transcript review UI over the live chat. */
        const val TRANSCRIPTS_SEGMENT = "transcripts"
    }
}
